import { QueryClient } from '@tanstack/react-query';

/**
 * Shared QueryClient singleton.
 *
 * Tenant-scoped list queries historically used keys without `tenantId`
 * (e.g. `['users', page, …]`). Combined with a 5-minute staleTime and logout
 * that did not clear this cache, switching accounts in the same SPA session
 * could briefly show another tenant's data until a full page reload.
 *
 * Always call `clearQueryCacheOnAuthChange()` on logout and before applying a
 * new session so no prior-tenant cache can leak across identities.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

/** Wipe all React Query cache — required on every auth identity change. */
export function clearQueryCacheOnAuthChange(): void {
  queryClient.clear();
}

/**
 * Build a tenant-scoped query key. Prefer this for any firm-data list/detail
 * query so a cache entry cannot be reused after a tenant switch even if a
 * clear is missed.
 */
export function tenantQueryKey(
  tenantId: string | null | undefined,
  ...parts: unknown[]
): unknown[] {
  return ['tenant', tenantId ?? 'none', ...parts];
}
